import React, { useState, useRef, useCallback } from 'react';
import { AsyncPaginate } from 'react-select-async-paginate';
import debounce from 'lodash/debounce';
import { useDispatch } from 'react-redux';
import { fetchAsyncOffersList } from '../../../../redux/slices/managementSlice';
import { useTranslation } from 'react-i18next';
import { creatableAsyncSelectStyles } from '../GetLinksPopup/GetLinksPopup';

const CreatableAsyncOffersSelect = ({
                                        value,
                                        onChange,
                                        placeholder,
                                        isMulti = false,
                                        isInvalid
                                    }) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const [inputValue, setInputValue] = useState('');

    const debouncedFetch = useRef(
        debounce(async (search, callback) => {
            try {
                const result = await dispatch(fetchAsyncOffersList({ search })).unwrap();
                const list = Array.isArray(result.offers) ? result.offers : [];
                callback({
                    options: list.map(o => ({ label: o, value: o })),
                    hasMore: false,
                });
            } catch {
                callback({ options: [], hasMore: false });
            }
        }, 300)
    ).current;

    const loadOptions = useCallback(
        (search, _loaded, _additional) =>
            new Promise(resolve => {
                if (search && search.trim()) {
                    debouncedFetch(search, resolve);
                } else {
                    resolve({ options: [], hasMore: false });
                }
            }),
        [debouncedFetch]
    );

    // Оновлення inputValue + очищення value коли користувач повністю стер усе
    const handleInputChange = useCallback(
        (newVal, { action }) => {
            if (action === 'input-change') {
                setInputValue(newVal);
                if (newVal === '') {
                    // очищуємо вибране
                    onChange(isMulti ? [] : null, { action: 'clear' });
                }
            }
            return newVal;
        },
        [onChange, isMulti]
    );

    // Після вибору опції — очистити inputValue
    const handleChange = useCallback(
        (option, actionMeta) => {
            onChange(option, actionMeta);
            setInputValue(''); // щоб при повторному вводі запити йшли
        },
        [onChange]
    );

    const handleBlur = useCallback(() => {
        // створюємо нову опцію тільки якщо щось лишилось у inputValue
        if (
            inputValue.trim() &&
            (!value || (Array.isArray(value)
                ? !value.some(v => v.value === inputValue)
                : value.value !== inputValue))
        ) {
            const newOption = { label: inputValue, value: inputValue };
            handleChange(isMulti ? [newOption] : newOption, { action: 'create-option' });
        }
    }, [inputValue, value, handleChange, isMulti]);

    const handleFocus = useCallback(() => {
        const current = Array.isArray(value)
            ? '' // для мультивибору можна залишити пустим або взяти перший
            : value?.value || '';
        setInputValue(current);
    }, [value]);

    return (
        <AsyncPaginate
            value={value}
            loadOptions={loadOptions}
            onChange={handleChange}
            inputValue={inputValue}
            onInputChange={handleInputChange}
            onBlur={handleBlur}
            onFocus={handleFocus}                 // <-- ось тут
            placeholder={placeholder || t('type_offer')}
            cacheOptions
            defaultOptions={false}
            noOptionsMessage={() => t('no_options')}
            isValidNewOption={() => false}
            styles={creatableAsyncSelectStyles}
            additional={{ page: 1 }}
            isMulti={isMulti}
            cacheUniqs={[inputValue]}
            isInvalid={isInvalid}
        />
    );
};

export default CreatableAsyncOffersSelect;
